import { ChangeEvent, FC, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	CheckOutline,
	HStack,
	IconButton,
	Input,
	PencilOutline,
	Spinner,
	Tag,
	Text,
	Tooltip,
	TrashOutline,
	useToast,
	VStack,
	XOutline,
} from '@bonusly/design-system';

import { useBizyDeleteMemoryMutation, useBizyMemories, useBizyUpdateMemoryMutation } from '../../queries/bizy-memories';
import { BizyMemory } from '../../schemas/bizy-memories';

// ─── Memory Item ─────────────────────────────────────────────────────────────

interface MemoryItemProps {
	memory: BizyMemory;
}

export const MemoryItem: FC<MemoryItemProps> = ({ memory }) => {
	const { t } = useTranslation('bizy_settings');
	const [isEditing, setIsEditing] = useState(false);
	const [editText, setEditText] = useState(memory.text);
	const inputRef = useRef<HTMLInputElement>(null);
	const toast = useToast();

	const updateMutation = useBizyUpdateMemoryMutation();
	const deleteMutation = useBizyDeleteMemoryMutation();

	useEffect(() => {
		if (isEditing) {
			inputRef.current?.focus();
			inputRef.current?.select();
		}
	}, [isEditing]);

	const startEditing = () => {
		setEditText(memory.text);
		setIsEditing(true);
	};

	const cancelEditing = () => {
		setEditText(memory.text);
		setIsEditing(false);
	};

	const saveEdit = () => {
		const trimmed = editText.trim();
		setIsEditing(false);

		if (trimmed === memory.text || trimmed.length === 0) return;

		updateMutation.mutate(
			{ id: memory.id, text: trimmed },
			{
				onError: () => {
					toast({ title: t('memories.updateError'), status: 'error', duration: 3000 });
				},
			}
		);
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') saveEdit();
		if (e.key === 'Escape') cancelEditing();
	};

	const handleDelete = () => {
		deleteMutation.mutate(memory.id, {
			onError: () => {
				toast({ title: t('memories.deleteError'), status: 'error', duration: 3000 });
			},
		});
	};

	return (
		<Box borderBottom="1px solid" borderColor="border.soft" py={3}>
			<HStack spacing={2} mb={2} justify="space-between" align="center">
				<HStack spacing={2}>
					<Tag size="md" colorScheme="purple" variant="subtle">
						{memory.category}
					</Tag>
					<Tag size="md" variant="subtle">
						{memory.source}
					</Tag>
				</HStack>
				{!isEditing && (
					<HStack spacing={0} flexShrink={0}>
						<Tooltip label={t('memories.edit')} hasArrow={false}>
							<IconButton
								aria-label={t('memories.editMemory')}
								icon={<PencilOutline size="xs" />}
								size="xs"
								variant="ghost"
								color="secondary.fg"
								onClick={startEditing}
							/>
						</Tooltip>
						<Tooltip label={t('memories.delete')} hasArrow={false}>
							<IconButton
								aria-label={t('memories.deleteMemory')}
								icon={<TrashOutline size="xs" />}
								size="xs"
								variant="ghost"
								color="secondary.fg"
								isLoading={deleteMutation.isLoading}
								onClick={handleDelete}
							/>
						</Tooltip>
					</HStack>
				)}
			</HStack>

			{isEditing ? (
				<HStack spacing={1}>
					<Input
						ref={inputRef}
						size="sm"
						value={editText}
						onChange={(e: ChangeEvent<HTMLInputElement>) => setEditText(e.target.value)}
						onKeyDown={handleKeyDown}
						onBlur={saveEdit}
						maxLength={256}
						borderRadius="md"
						bg="bg.surface"
					/>
					<IconButton aria-label={t('save')} icon={<CheckOutline size="xs" />} size="xs" variant="ghost" onClick={saveEdit} />
					<IconButton
						aria-label={t('cancel')}
						icon={<XOutline size="xs" />}
						size="xs"
						variant="ghost"
						onMouseDown={(e) => e.preventDefault()}
						onClick={cancelEditing}
					/>
				</HStack>
			) : (
				<Text size="sm" color="text.default">
					{memory.text}
				</Text>
			)}
		</Box>
	);
};

// ─── Memories Section ────────────────────────────────────────────────────────

export const MemoriesSection = () => {
	const { t } = useTranslation('bizy_settings');
	const { data: memories, isLoading } = useBizyMemories();

	return (
		<Box>
			<Box pb={3} borderBottom="1px solid" borderColor="border.soft">
				<Text size="md" color="text.softest">
					{t('sectionIntro.memories')}
				</Text>
			</Box>

			{isLoading ? (
				<HStack justify="center" py={4}>
					<Spinner size="sm" />
				</HStack>
			) : !memories || memories.length === 0 ? (
				<Text size="sm" color="text.softer" pt={3}>
					{t('memories.empty')}
				</Text>
			) : (
				<VStack spacing={0} align="stretch">
					{memories.map((memory) => (
						<MemoryItem key={memory.id} memory={memory} />
					))}
				</VStack>
			)}
		</Box>
	);
};
